<script setup>
import { computed, nextTick, ref, shallowRef, watch } from 'vue';

// shadcn-style combobox for adding pages to a role menu: a button opens a
// popover with a search box and grouped results (Groups / Pages). It stays
// open after a pick so several pages can be added in a row; added pages
// simply drop out of the list.
const props = defineProps({
  pages: { type: Array, default: () => [] }, // pages that can still be added
  label: { type: String, default: 'Add page' },
  placeholder: { type: String, default: 'Search pages…' },
  emptyText: { type: String, default: 'Every page is already in this menu.' },
  size: { type: String, default: 'default' },
});
const emit = defineEmits(['pick']);

const open = shallowRef(false);
const query = shallowRef('');
const active = shallowRef(0);
const searchRef = ref(null);
const listRef = ref(null);

const isGroup = (p) => Number(p.isParent) === 1;

const filtered = computed(() => {
  const q = query.value.trim().toLowerCase();
  return props.pages.filter(
    (p) =>
      !q ||
      String(p.title || '').toLowerCase().includes(q) ||
      String(p.route || '').toLowerCase().includes(q),
  );
});

// Groups first, then pages; `flat` is the keyboard order.
const sections = computed(() =>
  [
    { title: 'Groups', items: filtered.value.filter(isGroup) },
    { title: 'Pages', items: filtered.value.filter((p) => !isGroup(p)) },
  ].filter((s) => s.items.length),
);
const flat = computed(() => sections.value.flatMap((s) => s.items));

watch(open, async (isOpen) => {
  if (!isOpen) return;
  query.value = '';
  active.value = 0;
  await nextTick();
  searchRef.value?.focus();
});
watch(query, () => {
  active.value = 0;
});
// Keep the highlight in range as picked pages disappear.
watch(flat, (list) => {
  if (active.value > list.length - 1) active.value = Math.max(0, list.length - 1);
});

function pick(page) {
  emit('pick', page.id);
  nextTick(() => searchRef.value?.focus());
}

function scrollActiveIntoView() {
  nextTick(() => {
    listRef.value
      ?.querySelector('[data-active="true"]')
      ?.scrollIntoView({ block: 'nearest' });
  });
}

function onKeydown(event) {
  const count = flat.value.length;
  if (event.key === 'ArrowDown') {
    event.preventDefault();
    if (count) active.value = (active.value + 1) % count;
    scrollActiveIntoView();
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    if (count) active.value = (active.value - 1 + count) % count;
    scrollActiveIntoView();
  } else if (event.key === 'Enter') {
    event.preventDefault();
    const page = flat.value[active.value];
    if (page) pick(page);
  } else if (event.key === 'Escape') {
    open.value = false;
  }
}
</script>

<template>
  <v-menu
    v-model="open"
    :close-on-content-click="false"
    location="bottom start"
    offset="6"
  >
    <template #activator="{ props: menuProps }">
      <v-btn
        v-bind="menuProps"
        variant="outlined"
        :size="size"
        prepend-icon="mdi-plus"
        :disabled="!pages.length"
        :title="pages.length ? '' : emptyText"
      >
        {{ label }}
      </v-btn>
    </template>

    <div class="cmd" role="dialog" :aria-label="label">
      <div class="cmd__search">
        <v-icon size="16">mdi-magnify</v-icon>
        <input
          ref="searchRef"
          v-model="query"
          type="text"
          :placeholder="placeholder"
          role="combobox"
          aria-autocomplete="list"
          :aria-expanded="true"
          @keydown="onKeydown"
        />
      </div>

      <div ref="listRef" class="cmd__list" role="listbox">
        <p v-if="!pages.length" class="cmd__empty">{{ emptyText }}</p>
        <p v-else-if="!flat.length" class="cmd__empty">No pages match “{{ query }}”.</p>

        <div v-for="section in sections" :key="section.title" class="cmd__group">
          <div class="cmd__heading">{{ section.title }}</div>
          <button
            v-for="page in section.items"
            :key="page.id"
            type="button"
            role="option"
            class="cmd__item"
            :data-active="flat[active] === page"
            :aria-selected="flat[active] === page"
            @mouseenter="active = flat.indexOf(page)"
            @click="pick(page)"
          >
            <v-icon size="16" class="cmd__icon">{{ page.icon || 'mdi-file-outline' }}</v-icon>
            <span class="cmd__title">{{ page.title }}</span>
            <span v-if="!isGroup(page)" class="cmd__route">{{ page.route }}</span>
          </button>
        </div>
      </div>

      <div v-if="flat.length" class="cmd__footer">
        <span><kbd>↑</kbd><kbd>↓</kbd> to move</span>
        <span><kbd>Enter</kbd> to add</span>
        <span><kbd>Esc</kbd> to close</span>
      </div>
    </div>
  </v-menu>
</template>

<style scoped>
.cmd {
  width: 340px;
  max-width: calc(100vw - 32px);
  overflow: hidden;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1);
  color: var(--foreground);
}

.cmd__search {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 12px;
  border-bottom: 1px solid var(--border);
}
.cmd__search .v-icon {
  color: var(--faint-foreground);
}
.cmd__search input {
  flex: 1;
  min-width: 0;
  height: 100%;
  border: 0;
  outline: none;
  background: transparent;
  font: inherit;
  font-size: 14px;
  color: var(--foreground);
}
.cmd__search input::placeholder {
  color: var(--faint-foreground);
}

.cmd__list {
  max-height: 300px;
  overflow-y: auto;
  padding: 4px;
}
.cmd__group + .cmd__group {
  margin-top: 4px;
  padding-top: 4px;
  border-top: 1px solid var(--border);
}
.cmd__heading {
  padding: 6px 8px 4px;
  font-size: 12px;
  font-weight: 500;
  color: var(--muted-foreground);
}
.cmd__item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 32px;
  padding: 6px 8px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  font: inherit;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
}
.cmd__item[data-active='true'] {
  background: var(--accent);
}
.cmd__icon {
  color: var(--muted-foreground);
}
.cmd__title {
  white-space: nowrap;
}
.cmd__route {
  flex: 1;
  min-width: 0;
  text-align: right;
  font-size: 12px;
  color: var(--faint-foreground);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cmd__empty {
  margin: 0;
  padding: 20px 12px;
  text-align: center;
  font-size: 14px;
  color: var(--muted-foreground);
}

.cmd__footer {
  display: flex;
  gap: 12px;
  padding: 8px 12px;
  border-top: 1px solid var(--border);
  background: var(--subtle);
  font-size: 12px;
  color: var(--muted-foreground);
}
.cmd__footer kbd {
  display: inline-block;
  min-width: 18px;
  margin-right: 2px;
  padding: 0 4px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: var(--card);
  font-family: inherit;
  font-size: 11px;
  text-align: center;
}
</style>
